<template>
  <v-card outlined class="mb-2">
    <v-card-title v-if="!useDefaultStyle" class="px-2 py-2">
      <div class="subtitle-2 font-weight-medium text-truncate">
        {{ result.performanceTarget.title }}
      </div>
      <v-spacer class=""></v-spacer>
      <performance-target-status-chip
        :status="result.status"
        small
        class="mt-1"
      ></performance-target-status-chip>
    </v-card-title>
    <v-card-text v-if="result.appliedException" class="pa-2">
      <div class="text-caption grey--text mb-1">
        Abweichende Einzelfallregelung angewendet
      </div>
      {{ result.appliedException.reason }}
    </v-card-text>

    <v-card-text v-if="useDefaultStyle" class="py-2">
      <v-row dense align="center">
        <v-col cols="12" md="5">
          <v-card-title class="px-0 mx-0 py-0">
            <div class="subtitle-2 font-weight-medium text-truncate">
              {{ result.performanceTarget.title }}
            </div>
            <v-spacer class="hidden-md-and-up"></v-spacer>
            <performance-target-status-chip
              :status="result.status"
              small
              class="mt-1 hidden-md-and-up"
            ></performance-target-status-chip>
          </v-card-title>
          <performance-target-status-chip
            :status="result.status"
            small
            class="mt-1 hidden-sm-and-down"
          ></performance-target-status-chip>
        </v-col>
        <v-col cols="12" md="7">
          <v-row dense>
            <v-col cols="6">
              <div class="text-caption grey--text">Ist-Wert</div>
              <div class="text-h6 font-weight-bold">
                {{ formatValue(result.value, result.unit) }}
              </div>
            </v-col>
            <v-col cols="6">
              <div class="text-caption grey--text">Soll-Wert</div>
              <div class="text-h6 font-weight-bold">
                {{
                  result.targetValue
                    ? formatValue(result.targetValue, result.unit)
                    : "–"
                }}
              </div>
            </v-col>
          </v-row>
          <!-- <v-progress-linear
            :value="getProgressPercentage(result)"
            :color="getStatusColor(result.status)"
            height="8"
            rounded
          ></v-progress-linear> -->
          <!-- Segmented Progress Bar -->
          <segmented-progress-bar
            :height="8"
            :primary-color="getSegmentColor()"
            :total-segments="result.targetValue || 1"
            :value="result.value || 0"
            class="mt-2"
          ></segmented-progress-bar>
          <div class="text-caption grey--text text-right mt-1">
            {{ getProgressPercentage(result) }}% erreicht
          </div>
        </v-col>
      </v-row>
    </v-card-text>
    <v-card-text v-else class="pa-2">
      <!-- Compact values row -->
      <v-row dense class="mb-1">
        <v-col cols="4" class="py-0">
          <div class="caption grey--text">IST</div>
          <div class="body-2 font-weight-bold">
            {{ formatValue(result.value, result.unit) }}
          </div>
        </v-col>
        <v-col cols="4" class="py-0">
          <div class="caption grey--text">SOLL</div>
          <div class="body-2 font-weight-bold">
            {{
              result.targetValue
                ? formatValue(result.targetValue, result.unit)
                : "–"
            }}
          </div>
        </v-col>
        <v-col cols="4" class="py-0 text-right">
          <div class="caption grey--text">FORTSCHRITT</div>
          <div class="body-2 font-weight-bold">
            {{ getProgressPercentage(result) }}%
          </div>
        </v-col>
      </v-row>

      <!-- Compact Segmented Progress Bar -->
      <!-- <v-progress-linear
        :value="getProgressPercentage(result)"
        :color="getStatusColor(result.status)"
        height="8"
        rounded
      ></v-progress-linear> -->

      <segmented-progress-bar
        :height="8"
        :primary-color="getSegmentColor()"
        :total-segments="result.targetValue || 1"
        :value="result.value || 0"
      ></segmented-progress-bar>
    </v-card-text>
    <v-divider v-if="showDetails"></v-divider>
    <v-card-text v-if="showDetails" class="pa-2">
      <div class="text-caption grey--text">Auswirkung auf Ampelstatus</div>
      <div class="text-body-2">
        {{ getRequiredForLevelText(result.requiredForLevel) }}
      </div>
    </v-card-text>
    <v-divider v-if="showDetails"></v-divider>
    <v-card-text v-if="showDetails" class="pa-2">
      <div class="text-caption grey--text">Betrachtungszeitraum</div>
      <div class="text-body-2">
        {{ getMonitoringIntervalText(result.monitoringInterval) }}
      </div>
    </v-card-text>
    <v-divider v-if="showDetails"></v-divider>
    <v-card-text v-if="showDetails" class="pa-2">
      <div class="text-caption grey--text">Nächster Cut-Off</div>
      <div class="text-body-2">
        {{ timestampConverter(result.nextCutOffDate) }} Uhr
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import PerformanceTargetStatusChip from "@/components/member-management/performance-targets/PerformanceTargetStatusChip.vue";
import SegmentedProgressBar from "@/components/common/SegmentedProgressBar.vue";

export default {
  name: "performance-target-result-card",
  components: {
    PerformanceTargetStatusChip,
    SegmentedProgressBar,
  },
  props: {
    result: {
      type: Object,
      required: true,
    },
    showDetails: {
      type: Boolean,
      default: false,
    },
    useDefaultStyle: {
      type: Boolean,
      default: false,
    },
  },

  methods: {
    getSegmentColor() {
      // Color based on status
      const status = this.result.status;
      const colors = {
        green: "#4CAF50",
        met: "#4CAF50",
        metByIndividualRuling: "#4CAF50",
        yellow: "#FF9800",
        red: "#ff5252",
        notMet: "#ff5252",
      };
      return colors[status] || "#9E9E9E";
    },
    timestampConverter(timestamp, includeTime = true) {
      if (!timestamp || !timestamp.toDate) {
        return "–";
      }
      const options = {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      };
      if (includeTime) {
        options.hour = "2-digit";
        options.minute = "2-digit";
      }
      return timestamp.toDate().toLocaleDateString("de-DE", options);
    },
    formatValue(value, unit) {
      if (value === null || value === undefined) {
        return "–";
      }

      const roundedValue = Math.round(value);

      if (!unit) {
        return roundedValue.toString();
      }

      // Format based on unit type
      switch (unit) {
        case "x":
          return `${roundedValue}x`;
        case "h":
          return `${roundedValue}h`;
        case "min":
          return `${roundedValue} min`;
        default:
          return `${roundedValue} ${unit}`;
      }
    },
    getStatusColor(status) {
      switch (status) {
        case "green":
        case "met":
          return "success";
        case "yellow":
          return "orange";
        case "red":
        case "notMet":
          return "error";
        default:
          return "grey";
      }
    },
    getProgressPercentage(target) {
      if (!target.targetValue || target.targetValue === 0) {
        return target.value > 0 ? 100 : 0;
      }
      const percentage = (target.value / target.targetValue) * 100;
      return Math.min(Math.round(percentage), 100);
    },
    getMonitoringIntervalText(monitoringInterval) {
      if (!monitoringInterval) {
        return "–";
      }
      const unit = monitoringInterval.unit;
      const count = monitoringInterval.value;

      const intervalTexts = {
        days: `Vorherige ${count} ${count === 1 ? "Tag" : "Tage"}`,
        weeks: `Laufende ${count} ${count === 1 ? "Woche" : "Wochen"}`,
        "weeks+current": `Vorherige ${count} ${
          count === 1 ? "Woche" : "Wochen"
        } + aktuelle Woche`,
        months: `Laufende ${count} ${count === 1 ? "Monat" : "Monate"}`,
        "months+current": `Vorherige ${count} ${
          count === 1 ? "Monat" : "Monate"
        } + aktueller Monat`,
      };

      return intervalTexts[unit] || "–";
    },
    getRequiredForLevelText(requiredForLevel) {
      if (!requiredForLevel) {
        return "Nicht für Ampelstatus erforderlich";
      }

      const levelTexts = {
        red: "Muss erfüllt sein für rote Ampel (Basis)",
        yellow: "Muss erfüllt sein für gelbe Ampel",
        green: "Muss erfüllt sein für grüne Ampel",
      };

      return levelTexts[requiredForLevel] || "Unbekannt";
    },
  },
};
</script>
